/**
 * The DSR workspace — `/v1/admin/users/{id}/dsr*` (docs/05-admin/22 §3.3, §5.4).
 *
 * **Nothing here can return the principal's data.** The workspace read is masked; an export is
 * queued and delivered to the principal, never to this console; a closure returns where the code
 * was sent, masked, and moves only when the principal's code comes back. Every mutation
 * invalidates the account's queries, so the history and the profile never disagree.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type DsrWorkspaceData = components['schemas']['DsrWorkspaceResponse'];
export type DsrRequest = components['schemas']['DsrRequestOut'];
export type DsrBlocker = components['schemas']['DsrBlockerOut'];
export type AssistedChannel = components['schemas']['DsrExportIn']['channel'];

export interface Intake {
  channel: AssistedChannel;
  ticket_code: string;
  reason: string;
}

export function useDsrWorkspace(userId: number) {
  return useQuery({
    queryKey: keys.users.dsr(userId),
    queryFn: () => authedClient.request<DsrWorkspaceData>(`/v1/admin/users/${userId}/dsr`),
  });
}

function useDsrMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.users.all() }),
  });
}

const base = (userId: number) => `/v1/admin/users/${userId}/dsr`;

export function useRequestExport(userId: number) {
  return useDsrMutation((intake: Intake) =>
    authedClient.request<DsrRequest>(`${base(userId)}/export`, { method: 'POST', body: intake }),
  );
}

export function useApplyCorrection(userId: number) {
  return useDsrMutation(
    (args: Intake & { profile?: components['schemas']['MePatchIn']; family?: components['schemas']['DsrFamilyCorrectionIn'][] }) =>
      authedClient.request<DsrRequest>(`${base(userId)}/correction`, { method: 'POST', body: args }),
  );
}

export function useRequestClosure(userId: number) {
  return useDsrMutation((intake: Intake) =>
    authedClient.request<components['schemas']['DsrClosureResponse']>(`${base(userId)}/closure`, {
      method: 'POST',
      body: intake,
    }),
  );
}

export function useVerifyClosure(userId: number) {
  return useDsrMutation(({ requestId, code }: { requestId: number; code: string }) =>
    authedClient.request<DsrRequest>(`${base(userId)}/${requestId}/verify`, {
      method: 'POST',
      body: { code },
    }),
  );
}

export function useCancelDsr(userId: number) {
  return useDsrMutation(({ requestId, reason }: { requestId: number; reason: string }) =>
    authedClient.request<DsrRequest>(`${base(userId)}/${requestId}/cancel`, {
      method: 'POST',
      body: { reason },
    }),
  );
}
